import { Link, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Tv, ListVideo, Package, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { apiClient } from '@/services/api';
import { formatDate } from '@/utils/cn';

export function PartnerClientDetailPage() {
  const { id } = useParams();
  const clientId = Number(id);

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ['partner-client', clientId],
    queryFn: () => apiClient.partner.getClient(clientId).then(r => r.data),
    enabled: !Number.isNaN(clientId),
  });

  const resendMut = useMutation({
    mutationFn: () => apiClient.partner.resendClientInvite(clientId),
    onSuccess: () => toast.success('Convite reenviado por e-mail.'),
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Falha ao reenviar convite.'),
  });

  if (isLoading) return <div className="container-app py-8 text-muted-foreground">Carregando…</div>;
  if (isError || !c) return (
    <div className="container-app py-8">
      <p className="text-muted-foreground">Cliente não encontrado.</p>
      <Link to="/parceiro" className="text-brand-600 hover:underline text-sm">← Voltar</Link>
    </div>
  );

  const st = c.stats ?? {};
  return (
    <div className="container-app py-8 space-y-6">
      <Link to="/parceiro" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Voltar
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">{c.nome_fantasia || c.razao_social}</h1>
          <p className="text-muted-foreground font-mono text-sm">{c.subdomain}</p>
        </div>
        <Badge variant={c.status === 'active' ? 'success' : c.status === 'trial' ? 'default' : 'muted'}>{c.status}</Badge>
      </div>

      <Card className="p-5">
        <h2 className="font-semibold mb-3">Cadastro</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <Field label="Razão social" value={c.razao_social} />
          <Field label="Nome fantasia" value={c.nome_fantasia} />
          <Field label="CNPJ/CPF" value={c.cnpj || c.cpf} />
          <Field label="Segmento" value={c.segment} />
          <Field label="Contato" value={c.contact_name} />
          <Field label="E-mail" value={c.email} />
          <Field label="Telefone" value={c.phone} />
          <Field label="Trial até" value={c.trial_ends_at ? formatDate(c.trial_ends_at) : null} />
          <Field label="Cadastrado em" value={formatDate(c.created_at)} />
        </dl>
        <div className="mt-4">
          <Button size="sm" variant="ghost" disabled={resendMut.isPending} onClick={() => resendMut.mutate()}>
            <Mail className="h-4 w-4 mr-1" /> Reenviar convite de acesso
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={<Tv className="h-5 w-5" />} label="TVs conectadas" value={`${st.tvs_online ?? 0}/${st.tvs_total ?? 0}`} highlight={(st.tvs_online ?? 0) > 0} />
        <StatCard icon={<ListVideo className="h-5 w-5" />} label="Playlists" value={st.playlists ?? 0} />
        <StatCard icon={<Package className="h-5 w-5" />} label="Produtos" value={st.products ?? 0} />
        <StatCard icon={<Mail className="h-5 w-5" />} label="Último acesso" value={st.last_user_login_at ? formatDate(st.last_user_login_at) : 'nunca'} />
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <dt className="text-xs uppercase text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value || '—'}</dd>
    </div>
  );
}

function StatCard({ icon, label, value, highlight }: { icon: React.ReactNode; label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <Card className="p-4">
      <div className={`flex items-center gap-2 ${highlight ? 'text-emerald-600' : 'text-muted-foreground'}`}>{icon}</div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </Card>
  );
}
